import PickImageModal from '@components/PickImageModal/PickImageModal';
import WhyWorkBlock from '@features/enterprise/components/WhyWorkBlock';
import { getCookie } from '@utils/getCookie';
import { FC, useState } from 'react';

import TemplateCompanyColorsModal from './TemplateCompanyColorsModal';
import UserDetailColorRow from './UserDetailColorRow';
import UserDetailImageRow from './UserDetailImageRow';
import { useEditTemplateImages } from '../hooks/useEditTemplateImages';
import { EnterpriseTemplate } from '../types';
import ConfirmationModal from './ConfirmationModal';
import TemplateWhyWorkBlockModal from './TemplateWhyWorkBlockModal';

type UpdateData = {
  label: string;
  type: string;
  formDataKey: string;
  defaultValue: string | null | undefined;
  isBackground?: boolean;
};

type TemplatePageCustomizationProps = {
  templateData: EnterpriseTemplate;
};

const TemplatePageCustomization: FC<TemplatePageCustomizationProps> = ({
  templateData,
}) => {
  const dontShowWarningPopup = getCookie('dontShowWarningPopup');
  const isAppliedChanges = templateData.apply_changes && !dontShowWarningPopup;
  const [pendingUpdate, setPendingUpdate] = useState<UpdateData | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isPickImageModalOpen, setIsPickImageModalOpen] = useState(false);
  const [isWhyWeWorkModalOpen, setIsUpdateWhyWeWorkModalOpen] = useState(false);

  const [imageType, setImageType] = useState<
    | ''
    | 'social_image'
    | 'logo'
    | 'cover_image'
    | 'profilePic'
    | 's_cover_image_file'
    | 'landing_page_images'
  >('');

  const [pendingImageType, setPendingImageType] = useState<
    | ''
    | 'social_image'
    | 'logo'
    | 'cover_image'
    | 'profilePic'
    | 's_cover_image_file'
    | 'landing_page_images'
  >('');

  const [errorText, setErrorText] = useState('');
  const [updateData, setUpdateData] = useState<UpdateData>({
    label: '',
    type: '',
    formDataKey: '',
    defaultValue: undefined,
    isBackground: false,
  });
  const { editTemplateImages, isUpdatingTemplateImages } =
    useEditTemplateImages({
      setErrorText,
    });

  const handleActionClick = (data: UpdateData) => {
    if (isAppliedChanges) {
      setPendingUpdate(data);
      setIsConfirmOpen(true);
    } else {
      setUpdateData(data);
    }
  };

  const handleConfirm = () => {
    if (pendingUpdate) {
      setUpdateData(pendingUpdate);
    } else if (pendingImageType) {
      setIsPickImageModalOpen(true);
      setImageType(pendingImageType);
    }
    setIsConfirmOpen(false);
    setPendingUpdate(null);
    setPendingImageType('');
  };

  const handleCancel = () => {
    setIsConfirmOpen(false);
    setPendingUpdate(null);
    setPendingImageType('');
  };
  return (
    <>
      <UserDetailImageRow
        label="Logo"
        value={templateData?.s_logo_file}
        action={() => {
          if (isAppliedChanges) {
            setIsConfirmOpen(true);
            setPendingImageType('logo');
          } else {
            setIsPickImageModalOpen(true);
            setImageType('logo');
          }
        }}
      />

      <UserDetailImageRow
        label="Background Image"
        value={templateData?.s_cover_image_file}
        tooltip="Uploading a background image will replace your background color. To revert to using a backroud color, simply delete your background image."
        template_id={templateData?.id}
        action={() => {
          if (isAppliedChanges) {
            setIsConfirmOpen(true);
            setPendingImageType('cover_image');
          } else {
            setIsPickImageModalOpen(true);
            setImageType('cover_image');
          }
        }}
      />

      <UserDetailImageRow
        label="Jobs Page Footer Images (Up to 3)"
        value={templateData?.additional_images}
        tooltip="Editing will remove old images. You can upload up to 3 new images."
        action={() => {
          if (isAppliedChanges) {
            setIsConfirmOpen(true);
            setPendingImageType('landing_page_images');
          } else {
            setIsPickImageModalOpen(true);
            setImageType('landing_page_images');
          }
        }}
      />

      <UserDetailImageRow
        label="Social Image"
        value={templateData?.s_social_image_file}
        action={() => {
          if (isAppliedChanges) {
            setIsConfirmOpen(true);
            setPendingImageType('social_image');
          } else {
            setIsPickImageModalOpen(true);
            setImageType('social_image');
          }
        }}
      />

      <UserDetailColorRow
        label="Background Color"
        value={templateData?.color1}
        tooltip="This is the color of your jobs page background. You can replace this color by uploading a background image above."
        action={() =>
          handleActionClick({
            label: 'Background Color',
            type: 'text',
            defaultValue: templateData?.color1,
            formDataKey: 'color1',
          })
        }
      />

      <UserDetailColorRow
        label="Primary Color"
        value={templateData?.color2}
        tooltip="This is the color of your Apply Now ctas and page footer."
        action={() =>
          handleActionClick({
            label: 'Primary Color',
            type: 'text',
            defaultValue: templateData?.color2,
            formDataKey: 'color2',
          })
        }
      />

      <UserDetailColorRow
        label="Secondary Color"
        value={templateData?.secondary_color}
        tooltip="This is the color of your unselected position titles and navigation header selections."
        action={() =>
          handleActionClick({
            label: 'Secondary Color',
            type: 'text',
            defaultValue: templateData?.secondary_color,
            formDataKey: 'secondary_color',
          })
        }
      />

      <UserDetailColorRow
        label="Background Blur"
        value={templateData?.background_blur}
        tooltip="This element is set to white at 20% opacity for optimal design balance. You can change the color if you prefer, but 20% opacity is recommended for best results."
        action={() =>
          handleActionClick({
            label: 'Background Blur',
            type: 'text',
            defaultValue: templateData?.background_blur,
            formDataKey: 'background_blur',
          })
        }
      />

      <WhyWorkBlock
        label="Why Work Here Block"
        tooltip="Blocks automatically resize to fit nicely based on how many you add. We recommend adding at least 3 blocks to this section."
        value={templateData?.why_we_work ?? null}
        action={() => setIsUpdateWhyWeWorkModalOpen(true)}
      />

      {/* Colors Update Modal */}
      <TemplateCompanyColorsModal
        templateData={templateData}
        label={updateData.label}
        type={updateData.type}
        formDataKey={updateData.formDataKey}
        defaultValue={updateData.defaultValue}
        isOpen={!!updateData.label}
        handleClose={() =>
          setUpdateData({
            label: '',
            type: '',
            formDataKey: '',
            defaultValue: undefined,
          })
        }
        isBackground={updateData.isBackground}
        blurOpacityDefault={templateData?.blur_opacity ?? 40}
      />

      {/* Images Update Modal */}
      <PickImageModal
        isOpen={isPickImageModalOpen}
        setIsOpen={setIsPickImageModalOpen}
        title={
          imageType === 'landing_page_images'
            ? 'Upload Landing Page Images'
            : imageType === 'social_image'
              ? 'Upload Social Image'
              : imageType === 'logo'
                ? 'Update Logo'
                : 'Upload Cover Image'
        }
        imageType={imageType}
        minWidth={320}
        minHeight={160}
        alertText={errorText}
        setAlertText={setErrorText}
        handleUpdateImage={editTemplateImages}
        isUpdatingImage={isUpdatingTemplateImages}
        template_id={templateData?.id}
        multipleImage={imageType === 'landing_page_images'}
      />

      {/* Why We Work Modal */}
      <TemplateWhyWorkBlockModal
        isOpen={isWhyWeWorkModalOpen}
        templateId={templateData?.id}
        whyWeWorkData={templateData?.why_we_work ?? null}
        handleClose={() => setIsUpdateWhyWeWorkModalOpen(false)}
      />

      {/* Global Edit Confirmation Modal */}
      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default TemplatePageCustomization;
